import { useContext } from "react";
import { useParams } from "react-router-dom";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { ProcedureUpdateNoticeRequest } from "@/features/competency/model/competence/ProcedureUpdateNoticeRequest";
import { ChecklistQueryKeys } from "@/features/procedures/api/checklist.queries";
import { ProcedureQueryKeys } from "@/features/procedures/api/procedure.queries";
import {
	UploadProcedureNodeDocumentRequest,
} from "@/features/procedures/model/procedure/ProcedureNodeDependency";
import { SuggestionRequest } from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import {
	DeleteProcedureChecklistRequest,
	ProcedureNodeSteps,
} from "@/features/procedures/model/procedure/ProcedureNodeSteps";
import {
	ImportProcedureNodeDocumentRequest,
	RefreshSharePointDocumentRequest,
} from "@/features/procedures/model/procedure/UpdateProcedureNodeRequest";
import { UpdateProcedureVersionRequest } from "@/features/procedures/model/procedure/UpdateProcedureVersionRequest";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { TaskQueryKeys } from "@/service/TaskQueries";

// TODO: Check update render!!!
export const PostProcedureUpdateSuggestions = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: SuggestionRequest) =>
			SATOSPortal.PostProcedureUpdateSuggestion(request),
		onMutate: async (newSuggestion: SuggestionRequest) => {
			await queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					TaskQueryKeys.procedureUpdatesSuggestions,
				],
			});

			const previousSuggestions = queryClient.getQueryData<unknown[]>([
				TaskQueryKeys.procedureUpdatesSuggestions,
			]);

			// Optimistically add the new suggestion to the current list
			queryClient.setQueryData<unknown[]>(
				[TaskQueryKeys.procedureUpdatesSuggestions],
				(old = []) => [...old, newSuggestion],
			);

			return { previousSuggestions };
		},
		onError: (_err, _newSuggestion, context) => {
			// Rollback to previous state if an error occurs
			if (context?.previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					context.previousSuggestions,
				);
			}
		},
		onSettled: () => {
			// Ensure that queries are refetched after mutation
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
			// Clears the cache for the checklist page
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useDeleteProcedureComment = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (request: {
			commentId: number;
			checklistId: number;
			stepId: number;
		}) => SATOSPortal.DeleteProcedureComment(request.commentId),
		onMutate: async (request) => {
			await queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					TaskQueryKeys.procedureUpdatesSuggestions,
				],
			});

			const previousSuggestions = queryClient.getQueryData<unknown[]>([
				TaskQueryKeys.procedureUpdatesSuggestions,
			]);

			// Optimistically remove the suggestion from the current list
			queryClient.setQueryData<unknown[]>(
				[TaskQueryKeys.procedureUpdatesSuggestions],
				(old = []) =>
					old.filter((suggestion) => {
						if (typeof suggestion !== "object" || suggestion === null)
							return true;
						const suggestionId =
							"id" in suggestion
								? (suggestion as { id?: unknown }).id
								: "Id" in suggestion
									? (suggestion as { Id?: unknown }).Id
									: undefined;
						return suggestionId !== request.commentId;
					}),
			);

			const commentsCacheKey = [
				ChecklistQueryKeys.checklist,
				request.checklistId,
				ChecklistQueryKeys.comments,
			];
			await queryClient.cancelQueries({ queryKey: commentsCacheKey });

			// Optimistically remove the suggestion from the current list
			queryClient.setQueryData(commentsCacheKey, (old: unknown) => {
				if (!Array.isArray(old)) return old;
				const next = old as unknown[];
				const commentsAtStep = next[request.stepId];
				if (!Array.isArray(commentsAtStep)) return next;
				const nextCopy = [...next];
				nextCopy[request.stepId] = commentsAtStep.filter((item) => {
					if (typeof item !== "object" || item === null) return true;
					const itemId =
						"Id" in item
							? (item as { Id?: unknown }).Id
							: "id" in item
								? (item as { id?: unknown }).id
								: undefined;
					return itemId !== request.commentId;
				});
				return nextCopy;
			});

			return { previousSuggestions };
		},
		onError: (_err, _id, context) => {
			// Rollback to previous state if an error occurs
			if (context?.previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					context.previousSuggestions,
				);
			}
		},
		onSettled: (_response, _error, request) => {
			// Ensure that queries are refetched after mutation
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
			// Clears the cache for the checklist page
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails],
			});
			void queryClient.invalidateQueries({
				queryKey: [
					ChecklistQueryKeys.checklist,
					request.checklistId,
					ChecklistQueryKeys.comments,
				],
			});
		},
	});
};

export const useDeleteProcedureUpdateSuggestionComment = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) =>
			SATOSPortal.DeleteProcedureUpdateSuggestionComment(id),

		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},

		onSettled: () => {
			// Ensure that queries are refetched after mutation
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
	});

	return mutation;
};

export const useAddProcedureUpdateSuggestionComment = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: Record<string, unknown>) =>
			SATOSPortal.AddProcedureUpdateSuggestionComment(request),
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
		onSettled: () => {
			// Refetch the updated list of comments
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
	});

	return mutation.mutateAsync;
};

const patchSuggestionList = <T extends { Id?: number }>(
	list: T[] | undefined,
	id: number,
	patch: Record<string, unknown>,
): T[] | undefined => {
	if (!Array.isArray(list)) return list;
	return list.map((item) => (item?.Id === id ? { ...item, ...patch } : item));
};

const patchSuggestionData = (
	data: unknown,
	id: number,
	patch: Record<string, unknown>,
): unknown => {
	if (!data) return data;
	if (Array.isArray(data)) {
		// When data is already a list of suggestions, we patch it directly.
		// We still rely on patchSuggestionList, but callers can compare the
		// returned array reference to detect no-op patches.
		return patchSuggestionList(data, id, patch);
	}
	if (typeof data === "object") {
		const obj = data as Record<string, unknown>;

		if (Array.isArray(obj.ProcedureUpdateSuggestions)) {
			const currentSuggestions = obj.ProcedureUpdateSuggestions as {
				Id?: number;
			}[];
			const nextSuggestions = reconcilePatchedSuggestions(
				currentSuggestions,
				id,
				patch,
			);

			// If nothing actually changed, preserve the original object reference.
			if (nextSuggestions === currentSuggestions) {
				return data;
			}

			return {
				...obj,
				ProcedureUpdateSuggestions: nextSuggestions,
			};
		}

		if (Array.isArray(obj.Entries)) {
			const currentEntries = obj.Entries as { Id?: number }[];
			const nextEntries = reconcilePatchedSuggestions(
				currentEntries,
				id,
				patch,
			);

			// If nothing actually changed, preserve the original object reference.
			if (nextEntries === currentEntries) {
				return data;
			}

			return {
				...obj,
				Entries: nextEntries,
			};
		}
	}
	return data;
};

/**
 * Helper that wraps patchSuggestionList and returns the original array
 * reference when no element has actually changed. This allows callers to
 * detect no-op patches via reference equality.
 */
const reconcilePatchedSuggestions = (
	suggestions: { Id?: number }[],
	id: number,
	patch: Record<string, unknown>,
): { Id?: number }[] => {
	const next = patchSuggestionList(suggestions, id, patch) as { Id?: number }[];

	// If patchSuggestionList is later optimized to sometimes return the
	// original array, respect that.
	if (next === suggestions) {
		return suggestions;
	}

	if (next.length !== suggestions.length) {
		return next;
	}

	for (let i = 0; i < suggestions.length; i += 1) {
		if (next[i] !== suggestions[i]) {
			return next;
		}
	}

	// All element references are identical; treat as a no-op and preserve
	// the original array reference.
	return suggestions;
};

const patchGroupedSuggestions = (
	data: unknown,
	id: number,
	patch: Record<string, unknown>,
): unknown => {
	if (!Array.isArray(data)) return data;

	let anyProcedureChanged = false;

	const nextProcedures = data.map((procedure) => {
		if (!procedure || typeof procedure !== "object") return procedure;

		const proc = procedure as Record<string, unknown>;
		const checklists = Array.isArray(proc.Checklists)
			? (proc.Checklists as Record<string, unknown>[])
			: [];

		let anyChecklistChanged = false;

		const nextChecklists = checklists.map((checklist) => {
			const checklistObj = checklist as Record<string, unknown>;
			const suggestions = Array.isArray(checklistObj.ProcedureUpdateSuggestions)
				? (checklistObj.ProcedureUpdateSuggestions as { Id?: number }[])
				: [];

			if (suggestions.length === 0) {
				return checklist;
			}

			const nextSuggestions = reconcilePatchedSuggestions(
				suggestions,
				id,
				patch,
			);

			if (nextSuggestions === suggestions) {
				return checklist;
			}

			anyChecklistChanged = true;

			return {
				...checklistObj,
				ProcedureUpdateSuggestions: nextSuggestions,
			};
		});

		if (!anyChecklistChanged) {
			return procedure;
		}

		anyProcedureChanged = true;

		return {
			...proc,
			Checklists: nextChecklists,
		};
	});

	// If no procedure contained a matching suggestion id, return the original
	// array reference so callers can treat this as a no-op.
	return anyProcedureChanged ? nextProcedures : data;
};

export const usePutProcedureUpdateSuggestions = () => {
	const queryClient = useQueryClient();
	const user = useContext(UserContext);

	const mutation = useMutation({
		mutationFn: ({ id, request }: { id: number; request: SuggestionRequest }) =>
			SATOSPortal.PutProcedureUpdateSuggestion(id, request),
		onMutate: async (updatedSuggestion: {
			id: number;
			request: SuggestionRequest;
		}) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
			const checklistId = updatedSuggestion.request?.ChecklistId ?? 0;
			if (checklistId) {
				await queryClient.cancelQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						ProcedureQueryKeys.checklist,
						ProcedureQueryKeys.step,
						ProcedureQueryKeys.log,
						checklistId,
					],
				});
			}

			// Snapshot the previous value
			const previousSuggestions = queryClient.getQueryData([
				TaskQueryKeys.procedureUpdatesSuggestions,
			]);
			const previousChecklistSuggestions = checklistId
				? queryClient.getQueryData([
						ProcedureQueryKeys.procedureDetails,
						ProcedureQueryKeys.checklist,
						ProcedureQueryKeys.step,
						ProcedureQueryKeys.log,
						checklistId,
					])
				: undefined;

			const nowIso = new Date().toISOString();
			const patch: Record<string, unknown> = {
				Status: updatedSuggestion.request?.Status,
				StatusChangeOn: nowIso,
				StatusUserName: user?.Name ?? undefined,
			};
			if (
				String(updatedSuggestion.request?.Status ?? "").toLowerCase() ===
				"closed"
			) {
				patch.ClosedOn = nowIso;
				patch.ClosedByUserName = user?.Name ?? undefined;
			}

			// Optimistically update to the new value
			const updatedSuggestions = patchGroupedSuggestions(
				previousSuggestions,
				updatedSuggestion.id,
				patch,
			);
			if (updatedSuggestions !== previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					updatedSuggestions,
				);
			}
			if (checklistId) {
				const updatedChecklistSuggestions = patchSuggestionData(
					previousChecklistSuggestions,
					updatedSuggestion.id,
					patch,
				);
				if (updatedChecklistSuggestions !== previousChecklistSuggestions) {
					queryClient.setQueryData(
						[
							ProcedureQueryKeys.procedureDetails,
							ProcedureQueryKeys.checklist,
							ProcedureQueryKeys.step,
							ProcedureQueryKeys.log,
							checklistId,
						],
						updatedChecklistSuggestions,
					);
				}
			}

			return { previousSuggestions, previousChecklistSuggestions, checklistId };
		},
		onError: (_err, _updatedSuggestion, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					context.previousSuggestions,
				);
			}
			if (context?.checklistId && context?.previousChecklistSuggestions) {
				queryClient.setQueryData(
					[
						ProcedureQueryKeys.procedureDetails,
						ProcedureQueryKeys.checklist,
						ProcedureQueryKeys.step,
						ProcedureQueryKeys.log,
						context.checklistId,
					],
					context.previousChecklistSuggestions,
				);
			}
		},
		onSettled: (_data, _error, updatedSuggestion) => {
			// Always refetch after error or success:
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});

			const checklistId = updatedSuggestion?.request?.ChecklistId;
			if (checklistId) {
				void queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						ProcedureQueryKeys.checklist,
						ProcedureQueryKeys.step,
						ProcedureQueryKeys.log,
						checklistId,
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export { useUpdateStepStatus } from "@/features/procedures/api/checklist.mutations";

export { useUpdateNodeTreePosition } from "@/features/procedures/api/procedure.mutations";

export { useMutateDeleteProcedure } from "@/features/procedures/api/procedure.mutations";

export { useMutateUpdateProcedureSettings } from "@/features/procedures/api/procedure.mutations";

export const MergeFileLinks = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: number) => SATOSPortal.MergeFileLinks(request),

		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: number) => {
			void queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.fileMerge, request],
			});
			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([ProcedureQueryKeys.fileMerge, request]);
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.fileMerge, variables],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				void queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.fileMerge,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const CreateNewProcedureUpdateNotice = () => {
	const queryClient = useQueryClient();
	const { id } = useParams();

	const mutation = useMutation({
		mutationFn: (request: ProcedureUpdateNoticeRequest) => {
			return SATOSPortal.PostProcedureUpdateNotice(request);
		},
		onMutate: (request) => {
			void queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, request.ProcedureId],
			});

			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedureKnowledge,
				request.ProcedureId,
			]);

			return { previousValues };
		},
		onError: (err, variables, context) => {
			if (context?.previousValues) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureKnowledge, variables.ProcedureId],
					context.previousValues,
				);
			}
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge],
			});
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, id],
			});
		},
	});

	return mutation.mutateAsync;
};

export { useCreateChecklist } from "@/features/procedures/api/checklist.mutations";

export { UpsertProcedureNodeSteps } from "@/features/procedures/api/checklist.mutations";

export const useDeleteChecklist = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DeleteProcedureChecklistRequest) =>
			SATOSPortal.DeleteProcedureChecklist(request),
		onMutate: (request: DeleteProcedureChecklistRequest) => {
			void queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.SchemeId,
				],
			});
			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
				]);

			return previousValue;
		},
		onSettled: (data, error, request) => {
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedure, request.ProcedureId],
			});
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});
			void queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					+request.SchemeId,
				],
			});
			void queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +request.SchemeId],
			});
		},
	});

	return mutation.mutateAsync;
};

// TODO: Ustin - Bad mutation, needs onMutate and onError
export const useMutateNodeDocumentVersion = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UpdateProcedureVersionRequest) =>
			SATOSPortal.UpdateNodeDocumentVersion(request),
		onSettled: (data, err, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.document],
			});
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedure, variables.ProcedureId],
			});
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
			void queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist],
			});
		},
	});

	return mutation.mutateAsync;
};

export { useMutateReimportDocument } from "@/features/procedures/api/procedure.mutations";

// TODO: Ustin - Bad mutation, needs onMutate, onError and onSettled
export const useMutateUpdateSharepointDocuments = () => {
	const mutation = useMutation({
		mutationFn: () => SATOSPortal.UpdateSharepointDocuments(),
	});

	return mutation.mutateAsync;
};

export { SaveProcedureGroup } from "@/features/procedures/api/procedure.mutations";

export const UpdateProcedureNodeDocument = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: ImportProcedureNodeDocumentRequest) =>
			SATOSPortal.UpdateProcedureNodeDocument(request),
		onSettled: (variables) => {
			if (variables) {
				void queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.document,
						ProcedureQueryKeys.node,
						variables.SchemeId,
					],
				});
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.document],
				});
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureDetails],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

// TODO: Ustin - Bad mutation, needs onMutate and onError
export const RefreshSharePointDocument = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: RefreshSharePointDocumentRequest) =>
			SATOSPortal.RefreshSharePointDocument(request),
		onSettled: (variables) => {
			if (variables?.Success) {
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.document],
				});
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.details],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export const UploadProcedureNodeDocument = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UploadProcedureNodeDocumentRequest) =>
			SATOSPortal.UploadProcedureNodeDocument(request),
		onMutate: (request) => {
			return request;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.document,
					ProcedureQueryKeys.node,
					variables.SchemeId,
				],
				previousValues,
			);

			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
		onSettled: (variables) => {
			if (variables) {
				void queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
				void queryClient.invalidateQueries({
					queryKey: [
						[
							ProcedureQueryKeys.document,
							ProcedureQueryKeys.node,
							variables.SchemeId.toString(),
						],
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export { useMutateCreateNewProcedure } from "@/features/procedures/api/procedure.mutations";
